import React, { useEffect, useState } from 'react';
import {
  Typography, Box, Paper, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Snackbar, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, CircularProgress
} from '@mui/material';
import { Add, Edit, Delete } from '@mui/icons-material';

import { API_URL } from "../../config";

import { useAuth } from "../../App";
import { hasPermission } from "../../utils/permissions";
import Forbidden403 from "../../components/common/Forbidden403";

export default function LocationsPage() {
  const { auth, fetchWithAuth } = useAuth();
  // Permission check: superuser or has 'locations:list' permission
  const hasListPermission = hasPermission(auth?.user, "location.list");
  if (!hasListPermission) {
    return <Forbidden403 />;
  }
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState('create');
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [locationName, setLocationName] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });


  const fetchLocations = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetchWithAuth(`${API_URL}/locations/`);
      if (!res.ok) throw new Error('Failed to fetch locations');
      const data = await res.json();
      setLocations(data);
    } catch (e) {
      setError(e.message);
      setLocations([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLocations();
  }, []);

  const handleOpenDialog = (loc = null) => {
    setDialogMode(loc ? 'edit' : 'create');
    setSelectedLocation(loc);
    setLocationName(loc ? loc.name : '');
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedLocation(null);
    setLocationName('');
  };

  const handleSave = async () => {
    if (!locationName.trim()) {
      setSnackbar({ open: true, message: 'Name is required', severity: 'error' });
      return;
    }
    setLoading(true);
    try {
      let res;
      if (dialogMode === 'create') {
        res = await fetchWithAuth(`${API_URL}/locations/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ name: locationName }),
        });
      } else {
        res = await fetchWithAuth(`${API_URL}/locations/${selectedLocation.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ name: locationName }),
        });
      }
      if (!res.ok) throw new Error('Failed to save location');
      setSnackbar({ open: true, message: dialogMode === 'create' ? 'Location created' : 'Location updated', severity: 'success' });
      handleCloseDialog();
      fetchLocations();
    } catch (e) {
      setSnackbar({ open: true, message: e.message, severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (loc) => {
    if (!window.confirm('Delete this location?')) return;
    setLoading(true);
    try {
      const res = await fetchWithAuth(`${API_URL}/locations/${loc.id}`, {
        method: 'DELETE',
      });
      if (!res.ok) throw new Error('Failed to delete location');
      setSnackbar({ open: true, message: 'Location deleted', severity: 'success' });
      fetchLocations();
    } catch (e) {
      setSnackbar({ open: true, message: e.message, severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>Manage Locations</Typography>
      <Button variant="contained" startIcon={<Add />} sx={{ mb: 2 }} onClick={() => handleOpenDialog()}>Add Location</Button>
      {error && <Typography color="error">{error}</Typography>}
      <TableContainer component={Paper} sx={{ maxWidth: 600 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={2}><CircularProgress size={24} /></TableCell></TableRow>
            ) : locations.length === 0 ? (
              <TableRow><TableCell colSpan={2}>No locations found</TableCell></TableRow>
            ) : (
              locations.map((loc) => (
                <TableRow key={loc.id}>
                  <TableCell>{loc.name}</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => handleOpenDialog(loc)}><Edit fontSize="small" /></IconButton>
                    <IconButton size="small" color="error" onClick={() => handleDelete(loc)}><Delete fontSize="small" /></IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="xs" fullWidth>
        <DialogTitle>{dialogMode === 'edit' ? 'Edit Location' : 'Add Location'}</DialogTitle>
        <DialogContent>
          <TextField
            label="Location Name"
            value={locationName}
            onChange={(e) => setLocationName(e.target.value)}
            fullWidth
            autoFocus
            margin="dense"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">{dialogMode === 'edit' ? 'Update' : 'Create'}</Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
      />
    </Box>
  );
}
